import { LinkState, Provider } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSharedValue } from 'react-native-reanimated';
import Carousel, {
    type ICarouselInstance,
    Pagination,
} from 'react-native-reanimated-carousel';
import {
    BackButton,
    BackButtonIconType,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    H5Regular,
    Image,
    isWeb,
    View,
    YStack,
} from 'smoothie';

import Step1 from '../../../assets/images/onboarding/expenses-explainer-1.png';
import Step2 from '../../../assets/images/onboarding/expenses-explainer-2.png';
import Step3 from '../../../assets/images/onboarding/expenses-explainer-3.png';
import useConnectQuilttInstitution from '../../holistic/hooks/useConnectQuilttInstitution';
import { holisticAccountsAtom } from '../../holistic/state/HolisticAtoms';

const ExpensesExplainer = () => {
    const carouselRef = React.useRef<ICarouselInstance>(null);
    const progress = useSharedValue<number>(0);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [carouselWidth, setCarouselWidth] = useState(0);

    const carouselData = [Step1, Step2, Step3];

    const isOnLastSlide = useMemo(() => currentIndex === 2, [currentIndex]);

    const holisticAccounts = useAtomValue(holisticAccountsAtom);
    const activeAccounts = useMemo(() => {
        return holisticAccounts.filter((account) => {
            const isActiveQuilttAccount =
                account.provider === Provider.QUILTT &&
                (account.linkState === LinkState.CONNECTED ||
                    account.linkState === LinkState.CONNECTING);
            const isManual = account.provider === Provider.MANUAL;

            return isActiveQuilttAccount || isManual;
        });
    }, [holisticAccounts]);

    const { connectInstitution } = useConnectQuilttInstitution('onboarding');

    const FooterButton = () => {
        const onPress = useCallback(() => {
            if (isOnLastSlide) {
                router.navigate(
                    `/onboarding/connect-accounts${activeAccounts.length ? '' : '?openConnector=true'}`,
                );
            } else {
                carouselRef.current?.next({ animated: true });
            }
        }, [
            isOnLastSlide,
            carouselRef.current,
            activeAccounts.length,
            connectInstitution,
        ]);

        return (
            <Button
                text={isOnLastSlide ? 'Connect accounts' : 'Next'}
                variant={
                    isOnLastSlide
                        ? ButtonVariant.PRIMARY
                        : ButtonVariant.TERTIARY
                }
                size={ButtonSize.LARGE}
                onPress={onPress}
            />
        );
    };

    const onPressPagination = useCallback(
        (index: number) => {
            carouselRef.current?.scrollTo({
                count: index - progress.value,
                animated: true,
            });
        },
        [carouselRef.current, progress],
    );

    const ASPECT_RATIO = 1.2108;

    return (
        <FullScreenContainer
            header={
                <BackButton
                    fallbackRoute="/home"
                    iconType={BackButtonIconType.ARROW_LEFT}
                />
            }
            footer={<FooterButton />}
        >
            <YStack
                gap={isWeb ? 80 : 40}
                paddingVertical={24}
                onLayout={(e) => {
                    setCarouselWidth(e.nativeEvent.layout.width);
                }}
            >
                <H5Regular>Let’s get an idea of your expenses!</H5Regular>
                {carouselWidth > 0 && (
                    <View alignItems="center" width="100%">
                        <Carousel
                            ref={carouselRef}
                            width={
                                carouselWidth > 500
                                    ? Math.min(carouselWidth * 0.5, 350)
                                    : carouselWidth
                            }
                            data={carouselData}
                            height={
                                (carouselWidth > 500
                                    ? Math.min(carouselWidth * 0.5, 350)
                                    : carouselWidth) * ASPECT_RATIO
                            }
                            onProgressChange={progress}
                            renderItem={({ item }) => (
                                <Image
                                    source={item}
                                    style={{
                                        width:
                                            carouselWidth > 500
                                                ? Math.min(
                                                      carouselWidth * 0.5,
                                                      350,
                                                  )
                                                : carouselWidth,
                                        height:
                                            (carouselWidth > 500
                                                ? Math.min(
                                                      carouselWidth * 0.5,
                                                      350,
                                                  )
                                                : carouselWidth) * ASPECT_RATIO,
                                        objectFit: 'contain',
                                        // @ts-expect-error this is valid for now
                                        resizeMode: 'contain',
                                    }}
                                />
                            )}
                            loop={false}
                            mode="parallax"
                            modeConfig={{
                                parallaxScrollingScale: 1,
                                parallaxScrollingOffset: 0,
                                parallaxAdjacentItemScale: 0.8,
                            }}
                            onSnapToItem={(index) => {
                                setCurrentIndex(index);
                            }}
                        />
                        <Pagination.Basic
                            progress={progress}
                            data={carouselData}
                            dotStyle={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'white',
                                borderRadius: 4,
                            }}
                            activeDotStyle={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'black',
                                borderRadius: 4,
                            }}
                            containerStyle={{
                                gap: 8,
                                position: 'absolute',
                                bottom: 16,
                            }}
                            onPress={onPressPagination}
                        />
                    </View>
                )}
            </YStack>
        </FullScreenContainer>
    );
};

export default ExpensesExplainer;
